import { useState } from 'react';
import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import { Form, Formik, FormikConfig, Field } from 'formik';
import { useTranslation } from 'react-i18next';
import * as Yup from 'yup';

import { TextField, Switch, ColorPicker } from 'common/components/FormField';
import { Button } from 'common/components/Button';
import { DescriptionButton } from 'common/components/DescriptionButton';
import { DescriptionBox } from 'common/components/DescriptionBox';
import {
    IBasicFileFragment,
    ICreateLibraryArticleInput,
    IFileFragment,
    IUpdateLibraryArticleInput,
} from 'graphql/types';
import { ExitPageAlert } from 'common/components/ExitPageAlert';
import { UnsavedChangesAlert } from 'common/components/Alerts';
import { FormDeleteButton } from 'common/components/Button/FormDeleteButton';
import { Divider } from 'common/components/Divider';
import { LibraryHeaderImageField } from 'library/components/forms/LibraryHeaderImageField';
import { LibraryOverviewImageFormSection } from 'library/components/forms/LibraryOverviewImageFormSection';

export type TArticleFormValues = Omit<
    ICreateLibraryArticleInput | IUpdateLibraryArticleInput,
    'thumbnailId' | 'headerImageId'
> & {
    thumbnail?: IFileFragment | IBasicFileFragment | null;
    headerImage?: IFileFragment | IBasicFileFragment | null;
};
interface IProps extends FormikConfig<TArticleFormValues> {
    imageUploaded?: boolean;
    isDeleting?: boolean;
    onClose?(): void;
    onDelete?(): void;
}

export const LibraryArticleForm = ({
    imageUploaded,
    isDeleting,
    onClose,
    onDelete,
    ...other
}: IProps) => {
    const [translate] = useTranslation();
    const [featuredDescriptionOpen, setFeaturedDescriptionOpen] =
        useState(false);

    const validationSchema = Yup.object().shape({
        title: Yup.string().required(translate('validate.required')),
        description: Yup.string().optional(),
        titleColor: Yup.string().optional(),
    });

    return (
        <Formik validationSchema={validationSchema} {...other}>
            {({ submitForm, isSubmitting, values, dirty }) => (
                <>
                    <ExitPageAlert
                        alert={UnsavedChangesAlert}
                        when={
                            (dirty || imageUploaded) &&
                            !isSubmitting &&
                            !isDeleting
                        }
                        onConfirm={submitForm}
                    />

                    <Form>
                        <LibraryHeaderImageField />

                        <Grid container spacing={2}>
                            <Grid size="grow">
                                <Field
                                    component={TextField}
                                    label={translate('title')}
                                    name="title"
                                />
                            </Grid>
                            <Grid>
                                <Field
                                    component={ColorPicker}
                                    name="titleColor"
                                    popoverPosition="bottom-end"
                                    tooltip={translate('titleColor')}
                                />
                            </Grid>
                        </Grid>
                        <Field
                            multiline
                            component={TextField}
                            label={translate('description')}
                            name="description"
                        />

                        <Divider sx={{ mt: 2 }} />

                        <LibraryOverviewImageFormSection />

                        <Box sx={{ mt: 3 }}>
                            <Box sx={{ mb: 3 }}>
                                <Grid
                                    container
                                    sx={{
                                        alignItems: 'center',
                                    }}
                                >
                                    <Grid
                                        sx={{
                                            mr: -1,

                                            '> .MuiFormControl-root': {
                                                m: 0,
                                            },
                                        }}
                                    >
                                        <Field
                                            checked={values.featured}
                                            component={Switch}
                                            label={translate('featuredArticle')}
                                            name="featured"
                                        />
                                    </Grid>

                                    <Grid>
                                        <DescriptionButton
                                            onClick={() =>
                                                setFeaturedDescriptionOpen(
                                                    !featuredDescriptionOpen
                                                )
                                            }
                                        />
                                    </Grid>
                                </Grid>

                                <DescriptionBox
                                    open={featuredDescriptionOpen}
                                    onClose={() =>
                                        setFeaturedDescriptionOpen(false)
                                    }
                                >
                                    {translate(
                                        'libraryForm.featuredArticleDescription'
                                    )}
                                </DescriptionBox>
                            </Box>

                            <Divider />
                        </Box>

                        <Box sx={{ display: 'flex', mt: 2 }}>
                            <Button
                                color="primary"
                                disabled={
                                    isSubmitting ||
                                    (!isSubmitting && !dirty && !imageUploaded)
                                }
                                loading={isSubmitting}
                                type="submit"
                                variant="contained"
                                onClick={(
                                    e: React.MouseEvent<HTMLButtonElement>
                                ) => {
                                    e.preventDefault();

                                    submitForm();
                                }}
                            >
                                {translate('save')}
                            </Button>

                            {onClose && (
                                <Box sx={{ ml: 2 }}>
                                    <Button
                                        disabled={isSubmitting}
                                        variant="contained"
                                        onClick={onClose}
                                    >
                                        {translate('cancel')}
                                    </Button>
                                </Box>
                            )}

                            {!!onDelete && (
                                <Box sx={{ ml: 'auto' }}>
                                    <FormDeleteButton
                                        disabled={isSubmitting}
                                        onClick={onDelete}
                                    >
                                        {translate('delete')}
                                    </FormDeleteButton>
                                </Box>
                            )}
                        </Box>
                    </Form>
                </>
            )}
        </Formik>
    );
};
